import { create } from "zustand";

const GIS_SCRIPT_ID = "google-identity-services";
const GIS_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
// Used when GIS omits `expires_in`; Google access tokens last 60 minutes, so stay conservative.
const DEFAULT_TOKEN_LIFETIME_MS = 50 * 60 * 1000;
const GOOGLE_ADS_OAUTH_SCOPE = "https://www.googleapis.com/auth/adwords";
// Public OAuth client id (not a secret), hardcoded like the one in @similarweb/ga-connect.
const GOOGLE_ADS_OAUTH_CLIENT_ID = "209298878126-a0fj4qkek1fs1fldkf5s74uhkg5o665p.apps.googleusercontent.com";

interface IGoogleTokenResponse {
    access_token?: string;
    // Google documents this as a string ("3599"), so accept both.
    expires_in?: number | string;
    error?: string;
    error_description?: string;
}

interface IGoogleTokenClientErrorResponse {
    type: string;
    message?: string;
}

interface IGoogleTokenClient {
    requestAccessToken: (overrideConfig?: { prompt?: string }) => void;
}

declare global {
    interface Window {
        google?: {
            accounts: {
                oauth2: {
                    initTokenClient: (config: {
                        client_id: string;
                        scope: string;
                        callback: (response: IGoogleTokenResponse) => void;
                        error_callback?: (error: IGoogleTokenClientErrorResponse) => void;
                    }) => IGoogleTokenClient;
                };
            };
        };
    }
}

const isGisReady = (): boolean => Boolean(window.google?.accounts?.oauth2);

const loadGisScript = (): Promise<void> => {
    if (isGisReady()) {
        return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
        // A previous attempt may still be loading; wait on it instead of appending a second script.
        const existingScript = document.getElementById(GIS_SCRIPT_ID) as HTMLScriptElement | null;
        const script = existingScript ?? document.createElement("script");

        const handleLoad = (): void => {
            if (isGisReady()) {
                resolve();
                return;
            }
            script.remove();
            reject(new Error("Google Identity Services did not initialize on window.google"));
        };
        // A failed element must be removed, otherwise every retry would find it and never reload.
        const handleError = (): void => {
            script.remove();
            reject(new Error("Failed to load Google Identity Services script"));
        };

        script.addEventListener("load", handleLoad, { once: true });
        script.addEventListener("error", handleError, { once: true });

        if (!existingScript) {
            script.id = GIS_SCRIPT_ID;
            script.src = GIS_SCRIPT_SRC;
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        }
    });
};

// Not part of the reactive store state - nothing renders off this, it's just the singleton GIS
// client instance, shared module-wide so every `useGoogleAdsOAuth()` caller reuses the same one.
let tokenClient: IGoogleTokenClient | null = null;
// Bumped by reset(). A popup that is still open when reset() runs keeps its callbacks, so each
// client remembers the generation it was created in and ignores results from an older one.
let connectionGeneration = 0;

export interface IGoogleAdsOAuthStoreState {
    accessToken: string | null;
    // Millisecond timestamp after which `accessToken` is no longer valid. Null only while no token exists.
    expiresAt: number | null;
    // Non-secret stand-in for the token in query keys: bumps on every connect and reset, so cached
    // data is never served across a different Google connection.
    connectionId: number;
    isConnecting: boolean;
    error: string | null;
    connect: () => Promise<void>;
    reset: () => void;
}

export const isGoogleAdsTokenExpired = (state: Pick<IGoogleAdsOAuthStoreState, "expiresAt">): boolean =>
    state.expiresAt !== null && Date.now() >= state.expiresAt;

// Backing store for `useGoogleAdsOAuth` (SIM-104 follow-up fix). Previously each call site
// (`google-ads-import-wizard.tsx`, `create-campaign-wizard`, `use-google-ads-selection-action.ts`)
// called a hook that held its token in local `useState`, so a token acquired by one component tree
// was invisible to another, forcing a redundant OAuth prompt when the write flow (a separate route
// tree) tried to reuse a connection made moments earlier in the import flow. Backing the hook with
// this shared store fixes that: every caller reads/writes the same token.
// Deliberately not wrapped in `devtools`: that would serialize the access token to the Redux DevTools extension.
// Lets other files subscribe to "is connected" without ever reading the token value.
export const selectIsGoogleAdsConnected = (state: IGoogleAdsOAuthStoreState): boolean =>
    Boolean(state.accessToken) && !isGoogleAdsTokenExpired(state);

export const useGoogleAdsOAuthStore = create<IGoogleAdsOAuthStoreState>()((set) => ({
    accessToken: null,
    expiresAt: null,
    connectionId: 0,
    isConnecting: false,
    error: null,
    connect: async () => {
        set({ error: null, isConnecting: true });

        try {
            await loadGisScript();

            if (!window.google) {
                throw new Error("Google Identity Services did not initialize on window.google");
            }

            if (!tokenClient) {
                const clientGeneration = connectionGeneration;
                tokenClient = window.google.accounts.oauth2.initTokenClient({
                    client_id: GOOGLE_ADS_OAUTH_CLIENT_ID,
                    scope: GOOGLE_ADS_OAUTH_SCOPE,
                    callback: (response) => {
                        if (clientGeneration !== connectionGeneration) {
                            return;
                        }
                        if (response.error || !response.access_token) {
                            set({
                                isConnecting: false,
                                error: response.error_description ?? response.error ?? "Google Ads connect failed",
                            });
                            return;
                        }
                        const expiresInSeconds = Number(response.expires_in);
                        const lifetimeMs =
                            Number.isFinite(expiresInSeconds) && expiresInSeconds > 0
                                ? expiresInSeconds * 1000
                                : DEFAULT_TOKEN_LIFETIME_MS;
                        set((state) => ({
                            isConnecting: false,
                            accessToken: response.access_token,
                            expiresAt: Date.now() + lifetimeMs,
                            connectionId: state.connectionId + 1,
                        }));
                    },
                    error_callback: (tokenClientError) => {
                        if (clientGeneration !== connectionGeneration) {
                            return;
                        }
                        set({ isConnecting: false, error: tokenClientError.message ?? tokenClientError.type });
                    },
                });
            }

            tokenClient.requestAccessToken();
        } catch (caughtError) {
            set({
                isConnecting: false,
                error: caughtError instanceof Error ? caughtError.message : "Failed to start Google OAuth flow",
            });
        }
    },
    reset: () => {
        connectionGeneration += 1;
        tokenClient = null;
        set((state) => ({
            accessToken: null,
            expiresAt: null,
            connectionId: state.connectionId + 1,
            error: null,
            isConnecting: false,
        }));
    },
}));
